import moment from "moment";
import axios from "axios";

// モジュールスコープでキャッシュし、画面遷移・再描画をまたいで再取得しない
// 取得失敗時はキャッシュせず、次回呼び出しで再取得する。同時呼び出しは進行中のPromiseを共有する
let companyMasterListCache = null;
let carMasterListCache = null;
let companyMasterListPending = null;
let carMasterListPending = null;

// サインアウト・ユーザー切替時に前ユーザーのマスタを持ち越さないためのキャッシュ破棄
// 破棄前に開始した取得が後から完了しても、結果をキャッシュしないよう世代番号で判定する
let masterCacheEpoch = 0;
export function clearMasterCache() {
  masterCacheEpoch++;
  companyMasterListCache = null;
  carMasterListCache = null;
  companyMasterListPending = null;
  carMasterListPending = null;
}

export default {
  methods: {
    // Generic Utilities ================================================================
    // 企業マスタ(companyMaster)一覧を取得する（取得済みの場合はキャッシュを返す）
    async fetchCompanyMasterList(forceRefresh = false) {
      if (!forceRefresh && companyMasterListCache) {
        return companyMasterListCache;
      }
      if (!forceRefresh && companyMasterListPending) {
        return companyMasterListPending;
      }
      const epoch = masterCacheEpoch;
      const request = (async () => {
        try {
          const reqData = {
            headers: {
              Authorization: this.$store.getters.user.signInUserSession.idToken.jwtToken
            }
          };
          const response = await axios.get(this.createAPI("common/companymasterrequest"), reqData);
          const list = response.data.companyList ?? [];
          if (epoch === masterCacheEpoch) {
            companyMasterListCache = list;
          }
          return list;
        } catch (error) {
          console.error("企業マスタの取得に失敗しました:", error);
          return [];
        }
      })();
      companyMasterListPending = request;
      request.finally(() => {
        if (companyMasterListPending === request) {
          companyMasterListPending = null;
        }
      });
      return request;
    },
    // 車両マスタ(carMaster)一覧を取得する（取得済みの場合はキャッシュを返す）
    async fetchCarMasterList(forceRefresh = false) {
      if (!forceRefresh && carMasterListCache) {
        return carMasterListCache;
      }
      if (!forceRefresh && carMasterListPending) {
        return carMasterListPending;
      }
      const epoch = masterCacheEpoch;
      const request = (async () => {
        try {
          const reqData = {
            headers: {
              Authorization: this.$store.getters.user.signInUserSession.idToken.jwtToken
            }
          };
          const response = await axios.get(this.createAPI("common/carmasterrequest"), reqData);
          const list = response.data.carList ?? [];
          if (epoch === masterCacheEpoch) {
            carMasterListCache = list;
          }
          return list;
        } catch (error) {
          console.error("車両マスタの取得に失敗しました:", error);
          return [];
        }
      })();
      carMasterListPending = request;
      request.finally(() => {
        if (carMasterListPending === request) {
          carMasterListPending = null;
        }
      });
      return request;
    },
    hex2rgb(hex) {
      // Case of Long(Ex:#FF0000)
      let r = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
      let c = null;
      if (r) {
        c = r.slice(1, 4).map(function(x) {
          return parseInt(x, 16);
        });
      }
      // Case of Short(Ex:#F00)
      r = hex.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
      if (r) {
        c = r.slice(1, 4).map(function(x) {
          return 0x11 * parseInt(x, 16);
        });
      }
      // Not Exist then Return Null
      if (!c) {
        return null;
      }
      return `${c[0]}, ${c[1]}, ${c[2]}`;
    },
    createAPI(path) {
      const endpoint = import.meta.env.VITE_WEB_API_ENDPOINT ||
        (import.meta.env.VITE_API_ENDPOINT_PREFIX + import.meta.env.VITE_API_STAGE + import.meta.env.VITE_API_ENDPOINT_SUFFIX);
      return endpoint + "/" + path;
    },
    createInternalAPI(path) {
      const endpoint = import.meta.env.VITE_INTERNAL_API_ENDPOINT ||
        (import.meta.env.VITE_INTERNALAPI_ENDPOINT_PREFIX + import.meta.env.VITE_API_INT_STAGE + import.meta.env.VITE_INTERNALAPI_ENDPOINT_SUFFIX);
      return endpoint + "/" + path;
    },
    roundNumericValue(val, digit) {
      return Number(Number.parseFloat(val).toFixed(digit));
    },
    secondToHour(data, divisor, digit) {
      if (typeof data == "number") {
        return this.roundNumericValue(data / divisor, digit);
      } else {
        return data.map(elm => this.roundNumericValue(elm / divisor, digit));
      }
    },
    // Geo Location Utilities ===========================================================
    // Center Geo Location from Multi Points
    calculateCenterPoint(list) {
      var lon = 0;
      var lat = 0;
      for (var i in list) {
        lon = lon + parseFloat(list[i][0]);
        lat = lat + parseFloat(list[i][1]);
      }
      return [
        parseFloat(lon / (Number(i) + 1)),
        parseFloat(lat / (Number(i) + 1))
      ];
    },
    // Zoom in Area from Multi Points
    calculateFitBound(list) {
      var maxlon = list[0][0];
      var maxlat = list[0][1];

      var minlon = list[0][0];
      var minlat = list[0][1];
      for (var i in list) {
        if (list[i][0] > maxlon) {
          maxlon = list[i][0];
        } else if (list[i][0] < minlon) {
          minlon = list[i][0];
        }

        if (list[i][1] > maxlat) {
          maxlat = list[i][1];
        } else if (list[i][1] < minlat) {
          minlat = list[i][1];
        }
      }
      return [
        [minlon, maxlat],
        [maxlon, minlat]
      ];
    },
    // Station Info Utilities ===========================================================
    getConvertedStationStatus(orgtype, orgstatus) {
      var ret = { type: "", status: "" };
      // House - Full
      if (orgtype == "HouseholdGarbage" && orgstatus == "Full") {
        ret.type = "家庭ごみ";
        ret.status = "未回収";
      }
      // House - Empty
      else if (orgtype == "HouseholdGarbage" && orgstatus == "Empty") {
        ret.type = "家庭ごみ";
        ret.status = "回収済";
      }
      // Recycle(Collect) - Full
      else if (orgtype == "RecycleGarbage" && orgstatus == "Full") {
        ret.type = "資源ごみ";
        ret.status = "未回収";
      }
      // Recycle(Collect) - Empty
      else if (orgtype == "RecycleGarbage" && orgstatus == "Empty") {
        ret.type = "資源ごみ";
        ret.status = "回収/設置済";
      }
      // Recycle(Install) - Full
      else if (orgtype == "RecycleGarbage" && orgstatus == "NoReady") {
        ret.type = "資源ごみ";
        ret.status = "未設置";
      }
      // Recycle(Install) - Empty
      else {
        ret.type = "資源ごみ";
        ret.status = "回収/設置済";
      }
      return ret;
    },
    // Vehicle Info Utilities ===========================================================
    getConvertedVehicleStatus(status) {
      var ret = "停止中";
      // House - Full
      if (status == "Active") {
        ret = "稼働中";
      } else {
        ret = "停止中";
      }

      return ret;
    },

    getConvertedOperationStatus(status) {
      var ret = "作業終了";
      // House - Full
      if (status == "Active") {
        ret = "作業中";
      } else {
        ret = "作業終了";
      }

      return ret;
    },
    // Common Data Utilities ============================================================
    getCurrntEnvAndRegionFromURL(hostStrings) {
      var domain = hostStrings;
      var env = "unknown";
      var region = "unknown";

      // REGION
      // 室蘭市サブドメイン
      if (domain.match("muroran")) {
        region = "muroran";
      } else if (domain.match("kasukabe")) {
        region = "kasukabe";
      } else {
        region = "kasukabe";
      }

      // ENVIRONMENT
      if (domain.match("dev")) {
        env = "開発環境";
      } else if (domain.match("stg")) {
        env = "検証環境";
      } else if (domain.match("preprod")) {
        env = "プレ運用環境";
      } else if (domain.match("kaminoyama.garbage")) {
        env = "正式運用環境";
      } else {
        env = "不明";
      }

      return { env: env, region: region };
    },
    // ルートIDの企業No(companyMaster.dataNo)から企業名を解決する。companyMasterListに無い場合のみ旧体系の固定名にフォールバックする
    getCompanyFromId(id, companyMasterList) {
      var ret = {
        companyId: id,
        companyName: "パナソニックITS株式会社",
        garbagetype: "HouseholdGarbage"
      };
      const masterEntry = (companyMasterList ?? []).find(c => String(c.dataNo) === String(id));
      if (masterEntry) {
        ret.companyName = masterEntry.company;
      } else if (id == "0") {
        ret.companyName = "四日市市生活環境公社";
      } else if (id == "1") {
        ret.companyName = "函館市環境";
      }
      return ret;
    },
    getWeekdayDataFromId(id, type, garbageType, collecttype) {
      var ret = {
        weekdayId: id,
        weekdayType: type,
        weekdayJa: "日",
        weekdayTypaJa: "不明"
      };
      // Household Garbage
      if (garbageType == "HouseholdGarbage") {
        // if     (id == "0" && type=="0"){ret.weekdayJa = "月"; ret.weekdayTypaJa = "区分無し"}
        // else if(id == "1" && type=="0"){ret.weekdayJa = "火"; ret.weekdayTypaJa = "区分無し"}
        // else if(id == "3" && type=="0"){ret.weekdayJa = "木"; ret.weekdayTypaJa = "区分無し"}
        // else if(id == "4" && type=="0"){ret.weekdayJa = "金"; ret.weekdayTypaJa = "区分無し"}
        // else if(id == "2" && type=="1"){ret.weekdayJa = "水"; ret.weekdayTypaJa = "第1週"}
        // else if(id == "2" && type=="2"){ret.weekdayJa = "水"; ret.weekdayTypaJa = "第2週"}
        // else if(id == "2" && type=="3"){ret.weekdayJa = "水"; ret.weekdayTypaJa = "第3週"}
        // else                           {ret.weekdayJa = "日"; ret.weekdayTypaJa = "不明"}
        if     (id == "0"){ret.weekdayJa = "月"; ret.weekdayTypaJa = "区分無し"}
        else if(id == "1"){ret.weekdayJa = "火"; ret.weekdayTypaJa = "区分無し"}
        else if(id == "2"){ret.weekdayJa = "水"; ret.weekdayTypaJa = "区分無し"}
        else if(id == "3"){ret.weekdayJa = "木"; ret.weekdayTypaJa = "区分無し"}
        else if(id == "4"){ret.weekdayJa = "金"; ret.weekdayTypaJa = "区分無し"}
        else if(id == "5"){ret.weekdayJa = "土"; ret.weekdayTypaJa = "区分無し"}
        else              {ret.weekdayJa = "日"; ret.weekdayTypaJa = "不明"}
        // if (id == "0" && type == "0") {
        //   ret.weekdayJa = "月曜";
        //   ret.weekdayTypaJa = "";
        // } else if (id == "1" && type == "0") {
        //   ret.weekdayJa = "火曜";
        //   ret.weekdayTypaJa = "";
        // } else if (id == "3" && type == "0") {
        //   ret.weekdayJa = "木曜";
        //   ret.weekdayTypaJa = "";
        // } else if (id == "4" && type == "0") {
        //   ret.weekdayJa = "金曜";
        //   ret.weekdayTypaJa = "";
        // } else if (id == "2" && type == "1") {
        //   ret.weekdayJa = "第1水曜";
        //   ret.weekdayTypaJa = "";
        // } else if (id == "2" && type == "2") {
        //   ret.weekdayJa = "第2水曜";
        //   ret.weekdayTypaJa = "";
        // } else if (id == "2" && type == "3") {
        //   ret.weekdayJa = "第3水曜";
        //   ret.weekdayTypaJa = "";
        // } else {
        //   ret.weekdayJa = "日曜";
        //   ret.weekdayTypaJa = "";
        // }
      }
      // RecycleGarbage
      else {
        if (collecttype == "0") {
          // Collection
          if (id == "0" && type == "1") {
            ret.weekdayJa = "月曜1（不明）";
            ret.weekdayTypaJa = "";
          } else if (id == "0" && type == "2") {
            ret.weekdayJa = "月曜2（不明）";
            ret.weekdayTypaJa = "";
          } else if (id == "1" && type == "1") {
            ret.weekdayJa = "火曜1（絵鞆～幕西）";
            ret.weekdayTypaJa = "";
          } else if (id == "1" && type == "2") {
            ret.weekdayJa = "火曜2（中島～港北）";
            ret.weekdayTypaJa = "";
          } else if (id == "2" && type == "1") {
            ret.weekdayJa = "水曜1（寿～天神）";
            ret.weekdayTypaJa = "";
          } else if (id == "2" && type == "2") {
            ret.weekdayJa = "水曜2（海岸～母恋南）";
            ret.weekdayTypaJa = "";
          } else if (id == "3" && type == "1") {
            ret.weekdayJa = "木曜1（幌萌～石川）";
            ret.weekdayTypaJa = "";
          } else if (id == "3" && type == "2") {
            ret.weekdayJa = "木曜2（八丁平～柏木）";
            ret.weekdayTypaJa = "";
          } else if (id == "4" && type == "1") {
            ret.weekdayJa = "金曜1（御前水～東）";
            ret.weekdayTypaJa = "";
          } else if (id == "4" && type == "2") {
            ret.weekdayJa = "金曜2（高砂、水元）";
            ret.weekdayTypaJa = "";
          } else {
            ret.weekdayJa = "日曜（不明）";
            ret.weekdayTypaJa = "";
          }
        } else {
          // Installation
          if (id == "0" && type == "1") {
            ret.weekdayJa = "月曜1（絵鞆～幕西）";
            ret.weekdayTypaJa = "";
          } else if (id == "0" && type == "2") {
            ret.weekdayJa = "月曜2（中島～港北）";
            ret.weekdayTypaJa = "";
          } else if (id == "1" && type == "1") {
            ret.weekdayJa = "火曜1（寿～天神）";
            ret.weekdayTypaJa = "";
          } else if (id == "1" && type == "2") {
            ret.weekdayJa = "火曜2（海岸～母恋南）";
            ret.weekdayTypaJa = "";
          } else if (id == "2" && type == "1") {
            ret.weekdayJa = "水曜1（幌萌～石川）";
            ret.weekdayTypaJa = "";
          } else if (id == "2" && type == "2") {
            ret.weekdayJa = "水曜2（八丁平～柏木）";
            ret.weekdayTypaJa = "";
          } else if (id == "3" && type == "1") {
            ret.weekdayJa = "木曜1（御前水～東）";
            ret.weekdayTypaJa = "";
          } else if (id == "3" && type == "2") {
            ret.weekdayJa = "木曜2（高砂、水元）";
            ret.weekdayTypaJa = "";
          } else if (id == "4" && type == "1") {
            ret.weekdayJa = "金曜1（不明）";
            ret.weekdayTypaJa = "";
          } else if (id == "4" && type == "2") {
            ret.weekdayJa = "金曜2（不明）";
            ret.weekdayTypaJa = "";
          } else {
            ret.weekdayJa = "日曜（不明）";
            ret.weekdayTypaJa = "";
          }
        }
      }

      return ret;
    },
    getCollectionTypeFromId(id, companyId) {
      var ret = { collectionId: id, collectionType: "回収" };
      // if (companyId == "0") {
      //   if (id == "0") {
      //     ret.collectionType = "回収";
      //   } else if (id == "1") {
      //     ret.collectionType = "設置";
      //   } else {
      //     ret.collectionType = "不明";
      //   }
      // } else if (companyId == "1") {
      //   if (id == "0") {
      //     ret.collectionType = "燃やせるごみ";
      //   } else if (id == "1") {
      //     ret.collectionType = "燃えないごみ";
      //   } else if (id == "2") {
      //     ret.collectionType = "びん、缶、ペット";
      //   } else if (id == "3") {
      //     ret.collectionType = "プラスチック";
      //   } else {
      //     ret.collectionType = "不明";
      //   }
      // } else {
      //   if (id == "0") {
      //     ret.collectionType = "回収";
      //   } else if (id == "1") {
      //     ret.collectionType = "設置";
      //   } else {
      //     ret.collectionType = "不明";
      //   }
      // }
      return ret;
    },
    getCarNumberDataFromId(carnumber, carMasterList) {
      var carNumberList = (carMasterList ?? []).map(c => ({ value: String(c.dataNo), text: String(c.dataNo) }));
      for (var carNumber of carNumberList) {
        if (carnumber == carNumber.value) {
          return carNumber.text;
        }
      }
      return "";
    },
    // 選択企業のcompanyMaster.n4Options（特殊企業は週×曜日の組合せ）から、ダッシュボードの曜日フィルタと同じ選択肢を生成する
    buildCompanyDayOfWeekOptions(companyId, companyMasterList) {
      if (!companyId) {
        return [{ value: "", text: "すべて" }];
      }
      if (["5", "9"].includes(companyId)) {
        const specialDayOfWeekOptions = [
          '第1月', '第1火', '第1水', '第1木', '第1金',
          '第2月', '第2火', '第2水', '第2木', '第2金',
          '第3月', '第3火', '第3水', '第3木', '第3金',
          '第4月', '第4火', '第4水', '第4木', '第4金',
          '第5月',
        ];
        return [
          { value: "", text: "すべて" },
          ...specialDayOfWeekOptions.map(label => ({ value: label, text: label }))
        ];
      }
      const company = (companyMasterList ?? []).find(c => String(c.dataNo) == companyId);
      const n4Options = company?.n4Options ?? [];
      return [
        { value: "", text: "すべて" },
        ...n4Options.map(opt => ({ value: String(opt.value), text: opt.label }))
      ];
    },
    // buildCompanyDayOfWeekOptionsで選択された値(通常企業はn4値、特殊企業は「第1月」等)がルートのn4/n5と一致するか判定する
    isCompanyDayOfWeekMatched(selectedValue, companyId, n4, n5) {
      if (!selectedValue) {
        return true;
      }
      if (["5", "9"].includes(companyId)) {
        const weekdayMap = { '月': '0', '火': '1', '水': '2', '木': '3', '金': '4' };
        const matched = String(selectedValue).match(/^第(\d)(月|火|水|木|金)$/);
        return !!matched && n4 == weekdayMap[matched[2]] && n5 == matched[1];
      }
      return n4 == selectedValue;
    },
    // ルートIDのcompanyNo(N1)・N5(ルート番号)から、companyMaster.n5Optionsの表示文言(エリア名・週名)を取得する
    getRouteAreaLabel(companyNo, n5, companyMasterList) {
      const company = (companyMasterList ?? []).find(c => String(c.dataNo) == companyNo);
      if (!company) {
        return "";
      }
      const option = (company.n5Options ?? []).find(opt => String(opt.value) == n5);
      return option ? option.label : "";
    },
    getCollectionOptions() {
      return [
        { value: 0, text: "すべて" },
        { value: 2, text: "第 1収集地区" },
        { value: 3, text: "第 2収集地区" },
        { value: 4, text: "第 3収集地区" },
        { value: 5, text: "第 4収集地区" },
        { value: 6, text: "第 5収集地区" },
        { value: 7, text: "第 6収集地区" },
        { value: 8, text: "第 7収集地区" },
        { value: 9, text: "第 8収集地区" },
        { value: 10, text: "第 9収集地区" },
        { value: 11, text: "第10収集地区" },
        { value: 12, text: "第11収集地区" },
        { value: 13, text: "第12収集地区" },
      ];
    },
    getCompanyOptions(companyMasterList) {
      const list = companyMasterList ?? [];
      return [
        { value: "", text: "すべて" },
        ...list.map(c => ({ value: String(c.dataNo), text: c.company }))
      ];
    },
    getHouseholdCompanyOptions(companyMasterList) {
      const list = (companyMasterList ?? []).filter(c => (c.garbageKindNo ?? []).includes("0"));
      return [
        { value: "", text: "すべて" },
        ...list.map(c => ({ value: String(c.dataNo), text: c.company }))
      ];
    },
    getRecycleCompanyOptions(companyMasterList) {
      const list = (companyMasterList ?? []).filter(c => (c.garbageKindNo ?? []).some(k => k === "1" || k === "2"));
      return [
        { value: "", text: "すべて" },
        ...list.map(c => ({ value: String(c.dataNo), text: c.company }))
      ];
    },
    // ログインユーザーのcustom:company(companyMaster.dataNo)を企業フィルタの初期値に変換する
    // valueOffset: フィルタのvalueがdataNo+1の画面(レポート)では1を指定する。該当なしの場合はfallbackを返す
    getDefaultCompanyFilterValue(userCompanyNo, companyOptions, valueOffset = 0, fallback = "") {
      if (userCompanyNo === undefined || userCompanyNo === null || userCompanyNo === "" || isNaN(Number(userCompanyNo))) {
        return fallback;
      }
      const value = String(Number(userCompanyNo) + valueOffset);
      return (companyOptions ?? []).some(opt => opt.value !== fallback && String(opt.value) === value) ? value : fallback;
    },
    getReportCompanyOptions() {
      return [
        { value: "0", text: "すべて" },
        { value: "1", text: "四日市市生活環境公社" },
        // { value: "2", text: "函館市環境" },
        { value: "7", text: "パナソニックITS株式会社" }
      ];
    },
    getReportHouseholdCompanyOptions(companyMasterList) {
      // valueはAPI側で-1した値をcompanyNoとして使うため1〜(件数)にずらす
      const list = (companyMasterList ?? []).filter(c => (c.garbageKindNo ?? []).includes("0"));
      return [
        { value: "0", text: "すべて" },
        ...list.map(c => ({ value: String(Number(c.dataNo) + 1), text: c.company }))
      ];
    },
    getReportRecycleCompanyOptions(companyMasterList) {
      // valueはAPI側で-1した値をcompanyNoとして使うため1〜(件数)にずらす
      const list = (companyMasterList ?? []).filter(c => (c.garbageKindNo ?? []).some(k => k === "1" || k === "2"));
      return [
        { value: "0", text: "すべて" },
        ...list.map(c => ({ value: String(Number(c.dataNo) + 1), text: c.company }))
      ];
    },
    getReportManagementCompanyOptions(companyMasterList) {
      const list = (companyMasterList ?? []).filter(c => (c.garbageKindNo ?? []).some(k => k === "1" || k === "2"));
      return list.map(c => ({ value: String(c.dataNo), text: c.company }));
    },

    getOperatorNameList(companyId) {
      var prefix = "XX";
      var list = [{ value: "", text: "" }];
      if (companyId == "0") {
        prefix = "YK"; // Yokkaichi
      } else if (companyId == "1") {
        prefix = "DC"; // Dohnan Clean
      } else if (companyId == "2") {
        prefix = "AS"; // Asuma Syouzi
      } else if (companyId == "3") {
        prefix = "KS"; // Kasukabe Seisou
      } else if (companyId == "4") {
        prefix = "KE"; // Kyoei Eisei
      } else if (companyId == "5") {
        prefix = "MS"; // Muroran Seisou
      } else if (companyId == "6") {
        prefix = "CA"; // Muroran Seisou
      } else if (companyId == "7") {
        prefix = "MO"; // Muroran Seisou
      } else if (companyId == "8") {
        prefix = "PITS"; // Panasonic
      } else {
        // None
      }

      for (var i = 0; i < 12; i++) {
        var username = prefix + String(i + 1).padStart(2, "0");
        list.push({ value: username, text: username });
      }
      return list;
    },
    getDatePickerLabels() {
      return {
        labelNoDateSelected: "選択してください",
        labelNoTimeSelected: "未選択",
        labelHelp: "カーソルキーでも設定できます"
      };
    },
    getValidStatus(status) {
      var ret = "無効";
      // House - Full
      if (status == "0") {
        ret = "無効";
      } else {
        ret = "有効";
      }

      return ret;
    },
    getMonthOptions() {
      var current = moment();
      var year = current.year();
      var month = current.month();
      var since_year = 2022;
      var since_month = 9;

      var dataarray = [];

      for (var i = 0; i <= year - since_year; i++) {
        var selectedYear = since_year + i;
        // 最初の年は開始月からスタート
        if (i == 0) {
          for (var j = since_month; j < 12; j++) {
            var datemonth =
              String(selectedYear) + "/" + ("00" + (j + 1)).slice(-2);
            dataarray.push({
              value: datemonth,
              text: datemonth
            });
          }
        }
        //2年目以降は1月から表示対象
        else {
          if (year == selectedYear) {
            // 今月までカウント
            for (var j = 0; j <= month; j++) {
              var datemonth =
                String(selectedYear) + "/" + ("00" + (j + 1)).slice(-2);
              dataarray.push({
                value: datemonth,
                text: datemonth
              });
            }
          } else {
            // 12か月分カウント
            for (var j = 0; j < 12; j++) {
              var datemonth =
                String(selectedYear) + "/" + ("00" + (j + 1)).slice(-2);
              dataarray.push({
                value: datemonth,
                text: datemonth
              });
            }
          }
        }
      }

      return dataarray;
    },
    getYearOptions() {
      var current = moment();
      var year = current.year();
      var since_year = 2021;

      var dataarray = [];

      for (var i = 0; i <= year - since_year; i++) {
        var selectedYear = since_year + i;

        var dateyear = String(selectedYear);
        dataarray.push({
          value: dateyear,
          text: dateyear
        });
      }

      return dataarray;
    },
    getRecycleAreaOptions() {
      return [
        { value: "すべて", text: "すべて" },
        { value: "絵鞆～幕西", text: "絵鞆～幕西" },
        { value: "中島～港北", text: "中島～港北" },
        { value: "寿～天神", text: "寿～天神" },
        { value: "海岸～母恋南", text: "海岸～母恋南" },
        { value: "幌萌～石川", text: "幌萌～石川" },
        { value: "八丁平～柏木", text: "八丁平～柏木" },
        { value: "御前水～東", text: "御前水～東" },
        { value: "高砂、水元", text: "高砂、水元" }
      ];
    },
    getRecycleComponyOptions() {
      return [{ value: "すべて", text: "すべて" }];
    },
    getHouseholdComponyOptions() {
      return [
        { value: "すべて", text: "すべて" },
        { value: "四日市市生活環境公社", text: "四日市市生活環境公社" },
        // { value: "函館市環境", text: "函館市環境" }
      ];
    },
    // reportCompanyId(companyMaster.dataNo+1した値)に属する車両をcarMaster.companyNumber(=companyMaster.dataNo)から抽出する
    getReportVehicleOptionsForCompanies(reportCompanyId, carMasterList) {
      var cars = carMasterList ?? [];
      var ids = reportCompanyId ?? [];
      var list = [{ value: "", text: "すべて" }];

      if (ids.length === 0) {
        return list;
      }
      if (ids.includes("0")) {
        // 「すべて」選択時は全社の車両を対象にする
        list.push(...cars.map(c => ({ value: String(c.dataNo), text: String(c.dataNo) })));
        return list;
      }

      var companyNos = ids.map(id => String(Number(id) - 1));
      var matched = cars.filter(c => companyNos.includes(String(c.companyNumber)));
      list.push(...matched.map(c => ({ value: String(c.dataNo), text: String(c.dataNo) })));
      return list;
    },
    getHouseReportVehicleOptions(reportCompanyId, carMasterList) {
      return this.getReportVehicleOptionsForCompanies(reportCompanyId, carMasterList);
    },
    getRecycleReportVehicleOptions(reportCompanyId, carMasterList) {
      return this.getReportVehicleOptionsForCompanies(reportCompanyId, carMasterList);
    },

    getRecycleGarbageTypeOptions() {
      return [
        { value: "資源ごみ(設置)", text: "資源ごみ(設置)" },
        { value: "資源ごみ(回収)", text: "資源ごみ(回収)" }
      ];
    },
    // Web Utils
    showErrorMessageToast(title, msg, variant) {
      this.toastCount++;
      // Bootstrap Vue Next対応
      if (this.$bvToast) {
        // Vue 2 + BootstrapVue
        this.$bvToast.toast(msg, {
          title: title,
          autoHideDelay: 5000,
          variant: variant,
          appendToast: true
        });
      } else {
        // Vue 3 + Bootstrap Vue Next - フォールバック
        console.warn(`Toast: ${title} - ${msg}`);
        // 簡易的なアラート表示（実際のtoast実装まで）
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            alert(`${title}\n${msg}`);
          }, 100);
        }
      }
    },
    getLoadingCapacity(carMasterList) {
      return (carMasterList ?? []).map(c => ({ carNo: String(c.dataNo), payLoad: c.maximumPayload }));
    },
    getReportTemplate() {
      return {
        ServiceEndTime: "",
        ServiceEndTimeKind: "2",
        ServiceStartTime: "",
        ServiceStartTimeKind: "2",
        collectClassification: [
          {
            brownBottles: 0,
            dangerousObjects: 0,
            emptyCans: 0,
            otherBottes: 0,
            petBottles: 0,
            remarks: "",
            stationCounts: 0,
            transparentBottles: 0,
            workEndTime: "",
            workStartTime: ""
          },
          {
            brownBottles: 0,
            dangerousObjects: 0,
            emptyCans: 0,
            otherBottes: 0,
            petBottles: 0,
            remarks: "",
            stationCounts: 0,
            transparentBottles: 0,
            workEndTime: "",
            workStartTime: ""
          },
          {
            brownBottles: 0,
            dangerousObjects: 0,
            emptyCans: 0,
            otherBottes: 0,
            petBottles: 0,
            remarks: "",
            stationCounts: 0,
            transparentBottles: 0,
            workEndTime: "",
            workStartTime: ""
          }
        ],
        collectionArea: "",
        collectionDate: "",
        company: "",
        companyNumber: "",
        createAt: "",
        dataNo: "",
        dataType: "dailyReport",
        dirtObject: [
          {
            combustibleWeight: -1,
            kamishiroCheck: "0",
            loadingStatus: "0",
            nonburnableWeight: -1,
            workEndTime: "",
            workStartTime: ""
          },
          {
            combustibleWeight: -1,
            kamishiroCheck: "0",
            loadingStatus: "0",
            nonburnableWeight: -1,
            workEndTime: "",
            workStartTime: ""
          },
          {
            combustibleWeight: -1,
            kamishiroCheck: "0",
            loadingStatus: "0",
            nonburnableWeight: -1,
            workEndTime: "",
            workStartTime: ""
          },
          {
            combustibleWeight: -1,
            kamishiroCheck: "0",
            loadingStatus: "0",
            nonburnableWeight: -1,
            workEndTime: "",
            workStartTime: ""
          },
          {
            combustibleWeight: -1,
            kamishiroCheck: "0",
            loadingStatus: "0",
            nonburnableWeight: -1,
            workEndTime: "",
            workStartTime: ""
          },
          {
            combustibleWeight: -1,
            kamishiroCheck: "0",
            loadingStatus: "0",
            nonburnableWeight: -1,
            workEndTime: "",
            workStartTime: ""
          }
        ],
        driverName: ["", "", ""],
        garbageKindNo: "",
        installationClassification: [
          {
            brownBottles: 0,
            dangerousObjects: 0,
            emptyCans: 0,
            otherBottes: 0,
            petBottles: 0,
            remarks: "",
            stationCounts: 0,
            transparentBottles: 0,
            workEndTime: "",
            workStartTime: ""
          },
          {
            brownBottles: 0,
            dangerousObjects: 0,
            emptyCans: 0,
            otherBottes: 0,
            petBottles: 0,
            remarks: "",
            stationCounts: 0,
            transparentBottles: 0,
            workEndTime: "",
            workStartTime: ""
          },
          {
            brownBottles: 0,
            dangerousObjects: 0,
            emptyCans: 0,
            otherBottes: 0,
            petBottles: 0,
            remarks: "",
            stationCounts: 0,
            transparentBottles: 0,
            workEndTime: "",
            workStartTime: ""
          }
        ],
        operatorName: ["", "", ""],
        restTimeInwork: "",
        restTimeOutwork: "",
        routeId: "",
        targetNo: "",
        travelDistance: 0,
        travelDistanceKind: "2",
        allRestTime: ""
      };
    },

    // Report Data Utilities ============================================================
    getDashbardurlRequestID() {
      var ret = "ac67cae8-f5fc-43c5-b82a-0d2c155f5078"; // Staging - Report Dashboard
      if (process.env.API_INT_STAGE == "dev") {
        ret = "a1bd1cca-2c27-4bee-8881-8b6ec4ae43ee";
      } // Dev     - Report Dashboard
      else if (process.env.API_INT_STAGE == "stg") {
        ret = "ac67cae8-f5fc-43c5-b82a-0d2c155f5078";
      } // Staging - Report Dashboard
      else if (process.env.API_INT_STAGE == "") {
        ret = "07e967f0-3c19-40b1-85a5-a284a7971ed5";
      } // Prod    - Report Dashboard
      else {
      }
      return ret;
    },
    // 家庭ごみ集計
    getEmbedDashbordSheetIdOfHouseholdDaily() {
      var ret =
        "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_4f3996bb-ad78-4bc2-b2ad-35fd3cf22bc4"; // Staging - Report Dashboard
      if (process.env.API_INT_STAGE == "dev") {
        ret =
          "a1bd1cca-2c27-4bee-8881-8b6ec4ae43ee_df24a77d-3109-420c-96af-bcba76240fa2";
      } // Dev     - Report Dashboard
      else if (process.env.API_INT_STAGE == "stg") {
        ret =
          "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_4f3996bb-ad78-4bc2-b2ad-35fd3cf22bc4";
      } // Staging - Report Dashboard
      else if (process.env.API_INT_STAGE == "") {
        ret =
          "07e967f0-3c19-40b1-85a5-a284a7971ed5_bf3379f2-0926-43bb-8da2-fdf956008779";
      } // Prod    - Report Dashboard
      else {
      }
      return ret;
    },
    // 家庭ごみ月報(集計)
    getEmbedDashbordSheetIdOfHouseholdMonthly() {
      var ret =
        "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_f5732d8c-22dc-4124-b54e-0d078d1d3445"; // Staging - Report Dashboard
      if (process.env.API_INT_STAGE == "dev") {
        ret =
          "a1bd1cca-2c27-4bee-8881-8b6ec4ae43ee_fcf4074e-b925-4781-a729-a0c5774c227c";
      } // Dev     - Report Dashboard
      else if (process.env.API_INT_STAGE == "stg") {
        ret =
          "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_f5732d8c-22dc-4124-b54e-0d078d1d3445";
      } // Staging - Report Dashboard
      else if (process.env.API_INT_STAGE == "") {
        ret =
          "07e967f0-3c19-40b1-85a5-a284a7971ed5_2bc048cd-f317-4a8e-9fa7-e4c8957f1b7f";
      } // Prod    - Report Dashboard
      else {
      }
      return ret;
    },
    // 家庭ごみ年報(集計)
    getEmbedDashbordSheetIdOfHouseholdYearly() {
      var ret =
        "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_2f48c709-aec6-4718-b9a7-6d9a3e2e8446"; // Staging - Report Dashboard
      if (process.env.API_INT_STAGE == "dev") {
        ret =
          "a1bd1cca-2c27-4bee-8881-8b6ec4ae43ee_84809ba8-03af-4c37-adff-7a07f16d5b6a";
      } // Dev     - Report Dashboard
      else if (process.env.API_INT_STAGE == "stg") {
        ret =
          "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_2f48c709-aec6-4718-b9a7-6d9a3e2e8446";
      } // Staging - Report Dashboard
      else if (process.env.API_INT_STAGE == "") {
        ret =
          "07e967f0-3c19-40b1-85a5-a284a7971ed5_11aa2db7-4608-4cef-8482-ef09802b0e02";
      } // Prod    - Report Dashboard
      else {
      }
      return ret;
    },
    // 資源ごみ集計
    getEmbedDashbordSheetIdOfRecycleDaily() {
      var ret =
        "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_1e36f0c1-4192-411b-857f-378eaf753d50"; // Staging - Report Dashboard
      if (process.env.API_INT_STAGE == "dev") {
        ret =
          "a1bd1cca-2c27-4bee-8881-8b6ec4ae43ee_1ddc66b2-8be2-4c41-b9c5-8591ea561b65";
      } // Dev     - Report Dashboard
      else if (process.env.API_INT_STAGE == "stg") {
        ret =
          "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_1e36f0c1-4192-411b-857f-378eaf753d50";
      } // Staging - Report Dashboard
      else if (process.env.API_INT_STAGE == "") {
        ret =
          "07e967f0-3c19-40b1-85a5-a284a7971ed5_ec3d495c-4838-4d6b-be1d-b4b73f986367";
      } // Prod    - Report Dashboard
      else {
      }
      return ret;
    },
    // 資源ごみ月報(集計)
    getEmbedDashbordSheetIdOfRecycleMonthly() {
      var ret =
        "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_d413cb59-de4b-4b11-ac86-c701d41db927"; // Staging - Report Dashboard
      if (process.env.API_INT_STAGE == "dev") {
        ret =
          "a1bd1cca-2c27-4bee-8881-8b6ec4ae43ee_5b85610d-ff45-4a03-9665-d1ac76b3fc6e";
      } // Dev     - Report Dashboard
      else if (process.env.API_INT_STAGE == "stg") {
        ret =
          "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_d413cb59-de4b-4b11-ac86-c701d41db927";
      } // Staging - Report Dashboard
      else if (process.env.API_INT_STAGE == "") {
        ret =
          "07e967f0-3c19-40b1-85a5-a284a7971ed5_d9e0a3b9-6cbf-4bd8-a049-c21699e2eb52";
      } // Prod    - Report Dashboard
      else {
      }
      return ret;
    },
    // 資源ごみ年報(集計)
    getEmbedDashbordSheetIdOfRecycleYearly() {
      var ret =
        "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_b3a415fd-0462-4286-90d0-5bf1544eca24"; // Staging - Report Dashboard
      if (process.env.API_INT_STAGE == "dev") {
        ret =
          "a1bd1cca-2c27-4bee-8881-8b6ec4ae43ee_384bad44-e86e-43d6-a660-e218f7fb5f67";
      } // Dev     - Report Dashboard
      else if (process.env.API_INT_STAGE == "stg") {
        ret =
          "ac67cae8-f5fc-43c5-b82a-0d2c155f5078_b3a415fd-0462-4286-90d0-5bf1544eca24";
      } // Staging - Report Dashboard
      else if (process.env.API_INT_STAGE == "") {
        ret =
          "07e967f0-3c19-40b1-85a5-a284a7971ed5_9635b5f2-5b7e-4460-b1e2-3109bc3cab19";
      } // Prod    - Report Dashboard
      else {
      }
      return ret;
    },
    // Common Language Set Utilities ====================================================

    // GPS Utilities ====================================================================

    // AWS Utilities ====================================================================

    /**
     * ログ出力機能（1分ごとにS3のwaste-webapp-logバケットにログを出力）
     * @param {*} logLevel 基本的には"TOPIC"を使用
     * @param {*} message 出力するログメッセージ
     */
    putLogMessage(logLevel, message) {
      const nowDate = new Date();
      const nowDateStr = this.getYYYYMMDDHHMMSS(nowDate);
      message = "[" + logLevel + "] " + nowDateStr + " " + message + "\n";

      let messageList = [];
      const messageString = sessionStorage.getItem("logMessageList");
      if (messageString != null) {
        messageList = messageString.split(",");
      }

      messageList.push(message);
      sessionStorage.setItem("logMessageList", messageList);
    },

    /**
     * 日付→文字列変換
     * @param {*} date
     * @returns yyyy/MM/dd hh:mm:ss
     */
    getYYYYMMDDHHMMSS(date) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date
        .getDate()
        .toString()
        .padStart(2, "0");
      const hour = date
        .getHours()
        .toString()
        .padStart(2, "0");
      const minute = date
        .getMinutes()
        .toString()
        .padStart(2, "0");
      const second = date
        .getSeconds()
        .toString()
        .padStart(2, "0");

      return (
        year +
        "/" +
        month +
        "/" +
        day +
        " " +
        hour +
        ":" +
        minute +
        ":" +
        second
      );
    },
    isInteger(numVal) {
      // チェック条件パターン
      var pattern = /^([1-9]\d*|0)$/;
      // 数値チェック
      return pattern.test(numVal);
    },
    isDecimal(numVal) {
      // チェック条件パターン: 小数点以下最大4桁を許可
      var pattern = /^([1-9]\d*|0)(\.\d{1,3})?$/;
      // 数値チェック
      return pattern.test(numVal);
    },
    // ルートIDのcompanyNo(N1)・N4(収集区分)・N5(週)から、ダッシュボードの曜日フィルタと同じ文言を取得する
    getRouteWeekdayLabel(companyNo, n4, n5, companyMasterList) {
      const company = (companyMasterList ?? []).find(c => String(c.dataNo) == companyNo);
      if (!company) {
        return "";
      }
      // 県南(埋立等)・ドリーム(ペット)はn4Options/n5Optionsを使わず、週×曜日の組合せ文言(例:「第1月」)で表示する
      if (["5", "9"].includes(companyNo)) {
        const weekdayCharList = ["月", "火", "水", "木", "金"];
        const weekdayChar = weekdayCharList[Number(n4)];
        return (weekdayChar && n5) ? ("第" + n5 + weekdayChar) : "";
      }
      const option = (company.n4Options ?? []).find(opt => String(opt.value) == n4);
      return option ? option.label : "";
    },
  }
};
